import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Card, CardContent, CircularProgress, Divider, FormControlLabel,
  IconButton, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { chartDataService, ChartStyle } from '../services/chartDataService';
import { useGoogleSheets } from '../context/GoogleSheetsContext';
import { USE_CHART_DB } from '../config';

/**
 * Chart Settings — add/edit chart styles (the per-chart config that used to live
 * in the Google "Chart Config" sheet): JSON2Video template IDs, VO subfolder,
 * chart id, default outro text, accent/theme color, and the bilingual (EN/ES)
 * toggle. DB-mode only; in Sheets mode the Chart Config sheet remains the source
 * of truth and this page is not reachable (the menu entry is hidden).
 *
 * Name is shown read-only: it's the key the app/ETL link weekly data by, so
 * renaming would orphan a chart's weeks. (Create a new chart instead.)
 */

type Draft = Omit<ChartStyle, 'id'>;
const EMPTY_DRAFT: Draft = {
  name: '', horizontalTemplateId: '', verticalTemplateId: '', voSubfolder: '',
  chartId: '', defaultOutroText: '', themeColor: '#00FF9A', bilingual: false,
};

// A theme color is valid if empty (→ falls back to the app default) or a full
// #rrggbb hex. A malformed partial hex would produce broken CSS downstream
// (useThemeColors → NaN), so Save/Create are blocked until it's valid.
const validHex = (v: string): boolean => v.trim() === '' || /^#[0-9a-fA-F]{6}$/.test(v.trim());
// Normalize a color input to a valid #rrggbb for the <input type=color> swatch,
// without rejecting the free-text field (users may paste partial values).
const swatchColor = (v: string): string => (/^#[0-9a-fA-F]{6}$/.test(v) ? v : '#000000');

const ChartCard: React.FC<{
  initial: ChartStyle;
  onSaved: (c: ChartStyle) => void;
  onDeleted: (id: number) => void;
}> = ({ initial, onSaved, onDeleted }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [draft, setDraft] = useState<ChartStyle>(initial);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  useEffect(() => { setDraft(initial); }, [initial]);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(initial), [draft, initial]);
  // Only Draft keys are edited (never id); typed over Draft so it matches ChartFields.
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft(d => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const saved = await chartDataService.updateChart(initial.id, {
        horizontalTemplateId: draft.horizontalTemplateId,
        verticalTemplateId: draft.verticalTemplateId,
        voSubfolder: draft.voSubfolder,
        chartId: draft.chartId,
        defaultOutroText: draft.defaultOutroText,
        themeColor: draft.themeColor,
        bilingual: draft.bilingual,
      });
      onSaved(saved);
      enqueueSnackbar(`Saved "${saved.name}"`, { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(`Save failed: ${e instanceof Error ? e.message : String(e)}`, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await chartDataService.deleteChart(initial.id);
      enqueueSnackbar(`Deleted "${initial.name}"`, { variant: 'success' });
      onDeleted(initial.id);
    } catch (e) {
      // The server blocks deleting a chart that still has weeks (409) — surface it.
      enqueueSnackbar(e instanceof Error ? e.message : 'Delete failed', { variant: 'error' });
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{draft.name}</Typography>
          <FormControlLabel
            control={<Switch checked={draft.bilingual} onChange={(e) => set('bilingual', e.target.checked)} />}
            label="Bilingual (EN/ES)"
          />
        </Box>
        <ChartFields draft={draft} set={set} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          {/* Delete — two-step confirm. Server refuses if the chart has weeks. */}
          {confirmDelete ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="error">Delete “{initial.name}”?</Typography>
              <Button size="small" color="error" variant="contained" onClick={doDelete} disabled={deleting}>
                {deleting ? <CircularProgress size={16} /> : 'Confirm delete'}
              </Button>
              <Button size="small" onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancel</Button>
            </Box>
          ) : (
            <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setConfirmDelete(true)}>
              Delete chart
            </Button>
          )}
          <Button variant="contained" onClick={save} disabled={!dirty || saving || !validHex(draft.themeColor)}
            sx={{ backgroundColor: '#000', color: '#fff', '&:hover': { backgroundColor: '#333' } }}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

// Shared field grid for both edit cards and the create form.
const ChartFields: React.FC<{
  draft: Draft;
  set: <K extends keyof Draft>(k: K, v: Draft[K]) => void;
  includeName?: boolean;
}> = ({ draft, set, includeName }) => (
  <Stack spacing={2}>
    {includeName && (
      <TextField label="Chart name" value={draft.name} onChange={(e) => set('name', e.target.value)}
        fullWidth size="small" required helperText="Must be unique. Cannot be changed later (it links the chart's weeks)." />
    )}
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      <TextField label="Horizontal template ID" value={draft.horizontalTemplateId}
        onChange={(e) => set('horizontalTemplateId', e.target.value)} size="small" sx={{ flex: 1, minWidth: 240 }} />
      <TextField label="Vertical template ID" value={draft.verticalTemplateId}
        onChange={(e) => set('verticalTemplateId', e.target.value)} size="small" sx={{ flex: 1, minWidth: 240 }} />
    </Box>
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      <TextField label="VO subfolder" value={draft.voSubfolder} onChange={(e) => set('voSubfolder', e.target.value)}
        size="small" sx={{ flex: 1, minWidth: 200 }} helperText="S3 folder, e.g. hot-latin-songs" />
      <TextField label="Chart ID" value={draft.chartId} onChange={(e) => set('chartId', e.target.value)}
        size="small" sx={{ flex: 1, minWidth: 200 }} />
    </Box>
    <TextField label="Default outro text" value={draft.defaultOutroText}
      onChange={(e) => set('defaultOutroText', e.target.value)} fullWidth size="small" multiline minRows={2} />
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      <Tooltip title="Accent / theme color">
        <Box component="input" type="color" value={swatchColor(draft.themeColor)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => set('themeColor', e.target.value)}
          sx={{ width: 48, height: 40, border: '1px solid #ccc', borderRadius: 1, p: 0, cursor: 'pointer' }} />
      </Tooltip>
      <TextField label="Theme color (hex)" value={draft.themeColor} onChange={(e) => set('themeColor', e.target.value)}
        size="small" sx={{ width: 180 }} placeholder="#00FF9A"
        error={!validHex(draft.themeColor)}
        helperText={!validHex(draft.themeColor) ? 'Use #rrggbb or leave blank' : ' '} />
    </Box>
  </Stack>
);

export const ChartSettings: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { rediscover } = useGoogleSheets();
  const [charts, setCharts] = useState<ChartStyle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newDraft, setNewDraft] = useState<Draft>(EMPTY_DRAFT);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setCharts(await chartDataService.listCharts());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // After any change, refresh the app's chart config so the new settings (colors,
  // templates, bilingual toggle) take effect everywhere without a reload.
  const afterChange = useCallback(async () => {
    try { await rediscover(); } catch { /* non-fatal: settings still saved */ }
  }, [rediscover]);

  const onSaved = useCallback((saved: ChartStyle) => {
    setCharts(prev => prev.map(c => (c.id === saved.id ? saved : c)));
    afterChange();
  }, [afterChange]);

  const onDeleted = useCallback((id: number) => {
    setCharts(prev => prev.filter(c => c.id !== id));
    afterChange(); // refresh the app's chart config (a deleted chart drops from the picker)
  }, [afterChange]);

  const createNew = async () => {
    if (!newDraft.name.trim()) { enqueueSnackbar('Chart name is required', { variant: 'warning' }); return; }
    setCreating(true);
    try {
      const created = await chartDataService.createChart(newDraft);
      setCharts(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setNewDraft(EMPTY_DRAFT);
      setShowCreate(false);
      enqueueSnackbar(`Created "${created.name}"`, { variant: 'success' });
      afterChange();
    } catch (e) {
      enqueueSnackbar(`Create failed: ${e instanceof Error ? e.message : String(e)}`, { variant: 'error' });
    } finally {
      setCreating(false);
    }
  };

  if (!USE_CHART_DB) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 6, textAlign: 'center' }}>
        <Typography variant="h6">Chart settings are managed in the “Chart Config” sheet in this mode.</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ mt: 2 }}>Back</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 880, mx: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Tooltip title="Back to charts">
          <IconButton onClick={() => navigate('/')}><ArrowBackIcon /></IconButton>
        </Tooltip>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Chart Settings</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Add a chart style or edit its JSON2Video templates, VO subfolder, accent color, and bilingual toggle.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : error ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
          <Button variant="outlined" onClick={load}>Retry</Button>
        </Box>
      ) : (
        <>
          {charts.map(c => <ChartCard key={c.id} initial={c} onSaved={onSaved} onDeleted={onDeleted} />)}

          <Divider sx={{ my: 3 }} />

          {showCreate ? (
            <Card variant="outlined" sx={{ borderStyle: 'dashed' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>New chart</Typography>
                  <FormControlLabel
                    control={<Switch checked={newDraft.bilingual} onChange={(e) => setNewDraft(d => ({ ...d, bilingual: e.target.checked }))} />}
                    label="Bilingual (EN/ES)"
                  />
                </Box>
                <ChartFields draft={newDraft} set={(k, v) => setNewDraft(d => ({ ...d, [k]: v }))} includeName />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                  <Button onClick={() => { setShowCreate(false); setNewDraft(EMPTY_DRAFT); }} disabled={creating}>Cancel</Button>
                  <Button variant="contained" onClick={createNew} disabled={creating || !newDraft.name.trim() || !validHex(newDraft.themeColor)}
                    sx={{ backgroundColor: '#000', color: '#fff', '&:hover': { backgroundColor: '#333' } }}>
                    {creating ? <CircularProgress size={20} /> : 'Create chart'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Button variant="outlined" onClick={() => setShowCreate(true)}>+ Add a chart</Button>
          )}
        </>
      )}
    </Box>
  );
};

export default ChartSettings;
